import { useMemo, useCallback } from "react";
import { richTextToPlainText } from "../utils/richTextHelpers";

const LEGACY_DEFAULT_MODALITAETEN_TEXT = {
  "CT": "CT-spezifische Vorlage. Kann pro Benutzer individuell angepasst werden.",
  "MRT 1,5": "MRT-1,5T-spezifische Vorlage. Kann pro Benutzer individuell angepasst werden.",
  "MRT 3": "MRT-3T-spezifische Vorlage. Kann pro Benutzer individuell angepasst werden.",
};

const isOptionalMrtVariant = (name) => name === "MRT 1,5" || name === "MRT 3";

export default function useModalityDisplay({ modalities, vorlagen, customVorlagenGroups }) {
  const activeVorlagenModNames = useMemo(() => {
    const names = new Set();
    vorlagen.forEach((prompt) => {
      const mod = String(prompt?.mod || "").trim();
      if (!mod || mod === "Modalitäten" || mod === "Volumetrie") return;
      names.add(mod);
    });
    customVorlagenGroups.forEach((name) => {
      const normalizedName = String(name || "").trim();
      if (!normalizedName || normalizedName === "Modalitäten" || normalizedName === "Volumetrie") return;
      names.add(normalizedName);
    });
    return names;
  }, [customVorlagenGroups, vorlagen]);

  const modalityOptions = useMemo(() => {
    const byName = new Map();
    modalities.forEach((mod) => {
      if (!mod?.name) return;
      if (isOptionalMrtVariant(mod.name) && !activeVorlagenModNames.has(mod.name)) return;
      byName.set(mod.name, mod);
    });

    vorlagen
      .filter((prompt) => prompt?.mod === "Modalitäten" && prompt?.name)
      .forEach((prompt) => {
        if (isOptionalMrtVariant(prompt.name) && !activeVorlagenModNames.has(prompt.name)) return;
        if (byName.has(prompt.name)) return;
        byName.set(prompt.name, {
          name: prompt.name,
          prefill_text: prompt.befund || "",
        });
      });

    return Array.from(byName.values());
  }, [activeVorlagenModNames, modalities, vorlagen]);

  const modalityTree = useMemo(() => {
    const getModalityGroupKey = (name) => {
      const normalized = String(name || "").trim();
      if (!normalized) return "";

      const upper = normalized.toUpperCase();
      if (upper.startsWith("MRT")) return "MRT";
      if (upper.startsWith("CT")) return "CT";

      const firstToken = normalized.split(/[\s/_-]+/).filter(Boolean)[0] || normalized;
      return firstToken;
    };

    const nameSet = new Set();
    const nameOrder = [];

    modalityOptions.forEach((m) => {
      if (m.name && !nameSet.has(m.name)) {
        nameSet.add(m.name);
        nameOrder.push(m.name);
      }
    });

    vorlagen.forEach((v) => {
      const name = v.mod;
      if (name && name !== "Modalitäten" && name !== "Volumetrie" && !nameSet.has(name)) {
        nameSet.add(name);
        nameOrder.push(name);
      }
    });

    customVorlagenGroups.forEach((name) => {
      if (!name || name === "Modalitäten" || name === "Volumetrie" || nameSet.has(name)) return;
      nameSet.add(name);
      nameOrder.push(name);
    });

    const groupMap = new Map();
    const groupOrder = [];
    nameOrder.forEach((name) => {
      const group = getModalityGroupKey(name) || name;
      if (!groupMap.has(group)) {
        groupMap.set(group, []);
        groupOrder.push(group);
      }
      groupMap.get(group).push(name);
    });

    return groupOrder.map((group) => {
      const items = [...groupMap.get(group)].sort((a, b) => {
        if (a === group && b !== group) return -1;
        if (b === group && a !== group) return 1;
        return a < b ? -1 : 1;
      });
      return { group, items, single: items.length === 1 && items[0] === group };
    });
  }, [customVorlagenGroups, modalityOptions, vorlagen]);

  const getModalitaetenBefundLine = useCallback((modName, options = {}) => {
    const { allowLegacyDefault = false } = options;
    const normalizedMod = String(modName || "").trim();
    if (!normalizedMod) return "";

    const modalitaetenPrompt = vorlagen.find(
      (prompt) => prompt?.mod === "Modalitäten" && String(prompt?.name || "").trim() === normalizedMod
    );
    const line = richTextToPlainText(modalitaetenPrompt?.befund).trim();
    if (!line) return "";

    const legacyDefaultLine = LEGACY_DEFAULT_MODALITAETEN_TEXT[normalizedMod] || "";
    if (!allowLegacyDefault && legacyDefaultLine && line === legacyDefaultLine) {
      return "";
    }

    return line;
  }, [vorlagen]);

  return { modalityOptions, modalityTree, getModalitaetenBefundLine };
}
